import { Fragment, useState, type CSSProperties, type ReactNode } from 'react'
import { amountsFor, launchPrice, planPrice } from '../lib/pricing'
import { currencyLabel, formatMoney } from '../lib/currency'
import { userLocale } from '../lib/locale'
import { LANDING_COPY, LANDING_PATH, faqFor, type Cell, type LandingCopy, type LandingLang, type LandingMode } from '../lib/landingCopy'
import type { Prices } from '../hooks/usePrices'
import { CheckIcon, CloseIcon, PlusIcon } from './icons'
import { Wordmark } from './Wordmark'
import { Browser, Clip, Phone, Watch } from './DeviceFrames'

// The signed-out page in a browser: the design artifact's landing page
// (docs/matlist-kit, merge pack Landing.dc.html). Someone who installed the app
// has already bought the pitch, so LoginPage shows them only the form; a
// browser visitor reads this first, and the form waits at the very bottom —
// the e2e helpers click the *last* "Sign in", so it must stay last.
//
// Every word is in lib/landingCopy.ts, in English (/) and Swedish (/sv);
// scripts/prerender.mjs renders this same component into static HTML for
// search engines and agents. Say only what the product does today.

interface Props {
  prices: Prices
  /** Which language's page: from the path (landingLangOf). */
  lang?: LandingLang
  /** 'waitlist' before launch: the calls to action join the waitlist; pricing is shown just the same. */
  mode?: LandingMode
  /** Scroll to the form (with "Create account" selected when open). */
  onGetStarted: () => void
  /** Scroll to the form with "Sign in" selected. */
  onSignIn: () => void
  form: ReactNode
}

/** Lines of a display headline, broken where the design breaks them (with a space, so the text reads as words). */
function Lines({ lines, accent = false }: { lines: string[]; accent?: boolean }) {
  return <>{lines.map((l, i) => (
    <Fragment key={i}>{i > 0 && <>{' '}<br /></>}{accent && i === lines.length - 1 ? <span className="lp-accent">{l}</span> : l}</Fragment>
  ))}</>
}

export function Landing({ prices, lang = 'en', mode = 'open', onGetStarted, onSignIn, form }: Props) {
  const t = LANDING_COPY[lang]
  const waitlist = mode === 'waitlist'
  const cta = waitlist ? t.waitlist.cta : t.nav.open
  const other: LandingLang = lang === 'en' ? 'sv' : 'en'
  return (
    <div className="lp" lang={lang}>
      <header className="lp-nav lp-dark">
        <div className="lp-wrap lp-nav__inner">
          <Wordmark size={22} />
          <nav aria-label={t.nav.sections} className="lp-nav__links">
            <a href="#how" data-track="nav-how">{t.nav.how}</a>
            <a href="#recipes" data-track="nav-recipes">{t.nav.recipes}</a>
            <a href="#garmin" data-track="nav-garmin">{t.nav.garmin}</a>
            <a href="#pricing" data-track="nav-pricing">{t.nav.pricing}</a>
            <a href="#faq" data-track="nav-faq">{t.nav.faq}</a>
          </nav>
          <div className="lp-nav__actions">
            <a className="lp-link lp-lang" href={LANDING_PATH[other]} hrefLang={other} lang={other} data-track="nav-language">{t.nav.otherLang}</a>
            <button type="button" className="lp-link" data-track="nav-signin" onClick={onSignIn}>{t.nav.signIn}</button>
            <button type="button" className="lp-btn lp-btn--lime" data-track="nav-cta" onClick={onGetStarted}>{cta}</button>
          </div>
        </div>
      </header>

      <main>
        <section className="lp-hero lp-dark" aria-labelledby="lp-title" data-section="hero">
          <div className="lp-wrap lp-split">
            <div>
              <p className="lp-eyebrow">{t.hero.eyebrow}</p>
              <h1 id="lp-title" className="lp-h1"><Lines lines={t.hero.title} accent /></h1>
              <p className="lp-lead">{t.hero.lead}</p>
              <div className="lp-actions">
                <button type="button" className="lp-btn lp-btn--lime" data-track="hero-cta" onClick={onGetStarted}>{cta}</button>
                <a className="lp-btn lp-btn--ghost" href="#garmin" data-track="hero-garmin">{t.hero.garminCta}</a>
              </div>
            </div>
            <div className="lp-hero__art" aria-hidden="true">
              {/* A still moment before the trip: Home on the phone, the start screen on the watch. */}
              <Phone shot="shot-home" priority className="lp-hero__phone" />
              <Watch shot="watch-start" className="lp-hero__watch" />
            </div>
          </div>
          <div className="lp-wrap lp-works">
            <span className="lp-eyebrow">{t.hero.worksOn}</span>
            <ul>
              {t.hero.platforms.map(w => <li key={w}>{w}</li>)}
            </ul>
          </div>
        </section>

        <section className="lp-section lp-dark lp-film" aria-labelledby="lp-film" data-section="film">
          <div className="lp-wrap">
            <p className="lp-eyebrow">{t.film.eyebrow}</p>
            <h2 id="lp-film" className="lp-h2"><Lines lines={t.film.title} /></h2>
            <figure className="lp-film__frame">
              <Clip name="film-matlist" width={1280} height={720} />
              <figcaption className="lp-visually-hidden">{t.film.alt}</figcaption>
            </figure>
          </div>
        </section>

        <div id="how">
          <section id="garmin" className="lp-section lp-dark lp-chapter" aria-labelledby="lp-wrist" data-section="garmin" data-chapter="garmin">
            <div className="lp-wrap lp-split">
              <div>
                <ChapterHead n={1} label={t.garmin.eyebrow} id="lp-wrist" title={t.garmin.title} />
                <p className="lp-body">{t.garmin.body}</p>
                <p className="lp-stat"><b>{t.garmin.stat.value}</b><span>{t.garmin.stat.label}</span></p>
                <ul className="lp-points">
                  {t.garmin.points.map(pt => <Point key={pt} tint="accent">{pt}</Point>)}
                </ul>
                {/* TODO(human): the official "Find Matlist in the Connect IQ Store" badge and link, once the store listing is live. */}
              </div>
              <div className="lp-art" aria-hidden="true"><Watch shot="watch-list" clip="watch-shopping" className="lp-watch--large" /></div>
            </div>
          </section>

          <section className="lp-section lp-light lp-chapter" aria-labelledby="lp-store" data-section="store" data-chapter="store">
            <div className="lp-wrap lp-split lp-split--reverse">
              <div className="lp-art lp-store-art" aria-hidden="true">
                <Phone shot="shot-shopping" clip="phone-shopping" />
                <div className="lp-route">
                  <p className="lp-route__label">{t.store.routeLabel}</p>
                  <ol>
                    {t.store.route.map(([name, tint], i) => (
                      <li key={name}><span className="lp-route__n">{i + 1}</span><i style={{ background: `var(--ml-cat-${tint})` }} />{name}</li>
                    ))}
                  </ol>
                </div>
              </div>
              <div>
                <ChapterHead n={2} label={t.store.eyebrow} id="lp-store" title={t.store.title} />
                <p className="lp-body">{t.store.body}</p>
                <ul className="lp-points">
                  {t.store.points.map((pt, i) => (
                    <Point key={pt.lead} tint={(['produce', 'dairy', 'bakery'] as const)[i % 3]}><b>{pt.lead}</b> {pt.text}</Point>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="lp-section lp-lime lp-chapter" aria-labelledby="lp-shop" data-section="shop" data-chapter="together">
            <div className="lp-wrap lp-split">
              <div>
                <ChapterHead n={3} label={t.shop.eyebrow} id="lp-shop" title={t.shop.title} />
                <p className="lp-body">{t.shop.body}</p>
                <ul className="lp-points">
                  {t.shop.points.map(pt => <Point key={pt.lead} tint="ink"><b>{pt.lead}</b> {pt.text}</Point>)}
                </ul>
              </div>
              <div className="lp-art lp-together" aria-hidden="true">
                <Phone shot="shot-shopping" clip="phone-together" group="together" />
                <Watch shot="watch-list" clip="watch-together" group="together" className="lp-together__watch" />
              </div>
            </div>
          </section>

          <section id="recipes" className="lp-section lp-light lp-chapter" aria-labelledby="lp-recipes" data-section="recipes" data-chapter="recipes">
            <div className="lp-wrap lp-split lp-split--reverse">
              <div className="lp-art lp-recipe-art" aria-hidden="true">
                <ul className="lp-links">
                  {t.recipes.links.map(l => <li key={l}><LinkArrow />{l}</li>)}
                </ul>
                <Phone shot="shot-recipes" clip="phone-recipe" />
              </div>
              <div>
                <ChapterHead n={4} label={t.recipes.eyebrow} id="lp-recipes" title={t.recipes.title} />
                <p className="lp-body">{t.recipes.body}</p>
                <ul className="lp-points">
                  {t.recipes.points.map((pt, i) => (
                    <Point key={pt.lead} tint={(['produce', 'dairy', 'pantry'] as const)[i % 3]}><b>{pt.lead}</b> {pt.text}</Point>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        </div>

        <section className="lp-section lp-dark lp-more" aria-labelledby="lp-more" data-section="more">
          <div className="lp-wrap">
            <p className="lp-eyebrow">{t.more.eyebrow}</p>
            <h2 id="lp-more" className="lp-h2"><Lines lines={t.more.title} /></h2>
            <div className="lp-more__grid">
              <article className="lp-card lp-card--wide">
                <h3 className="lp-card__title">{t.plan.title.join(' ')}</h3>
                <p className="lp-card__body">{t.plan.body}</p>
                <Browser shot="shot-desktop" alt={t.plan.shotAlt} />
              </article>
              <article className="lp-card">
                <h3 className="lp-card__title">{t.usual.title.join(' ')}</h3>
                <p className="lp-card__body">{t.usual.body}</p>
                <div className="lp-chips__row" aria-hidden="true">
                  {t.usual.chips.map(([name, hint, tint]) => (
                    <span key={name} className="lp-chip" style={{ '--ml-tint': `var(--ml-cat-${tint})` } as CSSProperties}>
                      <PlusIcon size={16} />
                      <span><b>{name}</b><small>{hint}</small></span>
                      <i className="lp-chip__dot" />
                    </span>
                  ))}
                </div>
              </article>
              <article className="lp-card">
                <h3 className="lp-card__title">{t.more.summary.title}</h3>
                <p className="lp-card__body">{t.more.summary.body}</p>
                <dl className="lp-summary" aria-hidden="true">
                  {t.more.summary.stats.map(([value, label]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
                </dl>
              </article>
            </div>
          </div>
        </section>

        <section className="lp-section lp-dark" aria-labelledby="lp-setup" data-section="setup">
          <div className="lp-wrap">
            <h2 id="lp-setup" className="lp-h2"><Lines lines={t.setup.title} /></h2>
            <ol className="lp-steps">
              {t.setup.steps.map((st, i) => <Step key={st.title} n={`0${i + 1}`} title={st.title}>{st.text}</Step>)}
            </ol>
          </div>
        </section>

        <Compare t={t.compare} />

        <div id="pricing" className="lp-light lp-pricing" data-section="pricing">
          <div className="lp-wrap">
            <Pricing t={t.pricing} cta={waitlist ? t.waitlist.cta : t.pricing.cta} prices={prices} onGetStarted={onGetStarted} />
          </div>
        </div>

        <section id="faq" className="lp-section lp-dark" aria-labelledby="lp-faq" data-section="faq">
          <div className="lp-wrap lp-split lp-split--top">
            <h2 id="lp-faq" className="lp-h2">{t.faq.title}</h2>
            <dl className="lp-faq">
              {faqFor(lang, mode).map(f => <Faq key={f.q} q={f.q}>{f.a}</Faq>)}
            </dl>
          </div>
        </section>

        <section className="lp-cta" aria-labelledby="lp-cta" data-section="cta">
          <div className="lp-wrap lp-cta__inner">
            <h2 id="lp-cta" className="lp-cta__title"><Lines lines={t.cta.title} /></h2>
            <button type="button" className="lp-btn lp-btn--ink" data-track="band-cta" onClick={onGetStarted}>{cta}</button>
          </div>
        </section>

        <section className="lp-section lp-light lp-form" aria-label={waitlist ? t.waitlist.region : t.form.region} data-section="form">
          <div className="lp-form__inner">
            <p className="lp-eyebrow">{waitlist ? t.waitlist.eyebrow : t.form.eyebrow}</p>
            <h2 className="lp-h2 lp-h2--small">{waitlist ? t.waitlist.title : t.form.title}</h2>
            {waitlist && <p className="lp-form__body">{t.waitlist.body}</p>}
            {form}
            {waitlist && (
              <p className="lp-form__signin">
                {t.waitlist.member}{' '}
                <button type="button" className="ml-text-btn" data-track="form-signin" onClick={onSignIn}>{t.nav.signIn}</button>
              </p>
            )}
          </div>
        </section>
      </main>

      <footer className="lp-footer lp-dark">
        <div className="lp-wrap lp-footer__inner">
          <Wordmark size={18} />
          <span>&copy; 2026 Matlist</span>
          <div className="lp-footer__links">
            <a href={LANDING_PATH[other]} hrefLang={other} lang={other}>{t.nav.otherLang}</a>
            <a href="/terms.html">{t.footer.terms}</a>
            <a href="/privacy.html" rel="nofollow">{t.footer.privacy}</a>
          </div>
        </div>
      </footer>
    </div>
  )
}

// ── Comparison ────────────────────────────────────────────────────────────────

function Compare({ t }: { t: LandingCopy['compare'] }) {
  return (
    <section id="compare" className="lp-section lp-light" aria-labelledby="lp-compare" data-section="compare">
      <div className="lp-wrap">
        <div className="lp-split lp-split--top">
          <div>
            <p className="lp-eyebrow">{t.eyebrow}</p>
            <h2 id="lp-compare" className="lp-h2"><Lines lines={t.title} /></h2>
          </div>
          <p className="lp-body">{t.body}</p>
        </div>
        <div className="lp-compare__scroll">
          <table className="lp-compare">
            <thead>
              <tr>
                <td />
                {t.columns.map((c, i) => <th key={c} scope="col" className={i === 0 ? 'is-us' : undefined}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {t.rows.map(row => (
                <tr key={row.label}>
                  <th scope="row">{row.label}</th>
                  {row.cells.map((cell, i) => <td key={i} className={i === 0 ? 'is-us' : undefined}><CellMark cell={cell} yes={t.yes} no={t.no} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="lp-compare__note">{t.note}</p>
      </div>
    </section>
  )
}

function CellMark({ cell, yes, no }: { cell: Cell; yes: string; no: string }) {
  if (cell === true) return <span className="lp-compare__yes" role="img" aria-label={yes}><CheckIcon size={18} /></span>
  if (cell === false) return <span className="lp-compare__no" role="img" aria-label={no}><CloseIcon size={16} /></span>
  return <span className="lp-compare__text">{cell}</span>
}

// ── Pieces ────────────────────────────────────────────────────────────────────

/** The arrow on a shared link's pill. */
function LinkArrow() {
  return (
    <svg className="lp-links__arrow" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
      <path d="M3 9 9 3M4.5 3H9v4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/** A chapter's head, as in the film: the four-part bar with this chapter lit, its number and name, and the claim. */
function ChapterHead({ n, label, id, title }: { n: number; label: string; id: string; title: string[] }) {
  return (
    <>
      <div className="lp-chapter__bar" aria-hidden="true">{[1, 2, 3, 4].map(i => <i key={i} className={i <= n ? 'is-on' : undefined} />)}</div>
      <p className="lp-chapter__label"><span>{String(n).padStart(2, '0')}</span>{label}</p>
      <h2 id={id} className="lp-h2"><Lines lines={title} accent /></h2>
    </>
  )
}

function Point({ tint, children }: { tint: string; children: ReactNode }) {
  const color = tint === 'accent' ? 'var(--ml-accent)' : tint === 'ink' ? 'var(--matlist-ink)' : `var(--ml-cat-${tint})`
  return <li><i className="lp-dot" style={{ background: color }} aria-hidden="true" /><span>{children}</span></li>
}

function Step({ n, title, children }: { n: string; title: string; children: ReactNode }) {
  return (
    <li className="lp-step">
      <span className="lp-step__n" aria-hidden="true">{n}</span>
      <h3>{title}</h3>
      <p>{children}</p>
    </li>
  )
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return <div className="lp-faq__item"><dt>{q}</dt><dd>{children}</dd></div>
}

function SectionTitle({ id, eyebrow, title }: { id?: string; eyebrow: string; title: string }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <p className="lp-eyebrow">{eyebrow}</p>
      <h2 id={id} className="lp-h2 lp-h2--small">{title}</h2>
    </div>
  )
}

// ── Pricing (D1: the honest deal, kept above the form) ───────────────────────
// Nothing about the price is left for later: how the trial ends, Free and Plus
// side by side, and every term of the founding offer, in the viewer's currency.

function Pricing({ t, cta, prices, onGetStarted }: { t: LandingCopy['pricing']; cta: string; prices: Prices; onGetStarted: () => void }) {
  const locale = userLocale()
  const { table, currency, available } = prices
  const amounts = amountsFor(table, currency)
  // Same rule as SubscribeSheet: the base currency is what's shown when the
  // viewer's own isn't one the Prices carry, and Checkout may then convert.
  const converted = currency === table.base && available.length > 1 && !prices.usingFallback
  const [pickingCurrency, setPickingCurrency] = useState(false)
  // "under $1.30 a month": the annual price's monthly share, rounded up to the
  // next ten minor units so the claim is never an overstatement (planNote).
  const perMonth = formatMoney(Math.ceil(amounts.annual / 12 / 10) * 10, currency, locale)

  const yearly = planPrice('annual', amounts, currency, locale)

  return (
    <section style={{ marginBottom: 48 }} aria-labelledby="lp-pricing">
      <SectionTitle id="lp-pricing" eyebrow={t.eyebrow} title={t.title} />

      <h3 className="ml-label" style={{ color: 'var(--ml-text)', marginBottom: 8 }}>{t.howTitle}</h3>
      <ol className="lp-how">
        {t.how.map(step => <li key={step}>{step}</li>)}
      </ol>

      <div className="lp-plans">
        <PlanCard
          name={t.free.name}
          price={t.free.price}
          per={t.free.per}
          features={t.free.features}
        />
        <PlanCard
          name={t.plus.name}
          price={yearly}
          per={t.plus.perYear}
          note={t.plus.bestValue(perMonth)}
          alt={t.plus.orMonthly(planPrice('monthly', amounts, currency, locale))}
          features={t.plus.features}
          featured
          cta={cta}
          onCta={onGetStarted}
        />
      </div>

      <p style={{ color: 'var(--ml-text-secondary)', fontSize: 13, lineHeight: 1.5, marginTop: 16 }}>{t.small}</p>
      {t.founder && (
        <section className="lp-founder" aria-labelledby="lp-founder">
          <h3 id="lp-founder" className="lp-founder__title">{t.founder.title}</h3>
          <ul className="lp-founder__terms">
            {t.founder.points(launchPrice(amounts, currency, locale), yearly).map(point => <li key={point}>{point}</li>)}
          </ul>
        </section>
      )}
      <p style={{ color: 'var(--ml-text-secondary)', fontSize: 12, lineHeight: 1.5, marginTop: 8 }}>
        {t.tax(amounts.taxInclusive)}
        {converted && <> {t.conversion}</>}
      </p>

      {/* Which currency the amounts are in, and a way to change it — the
          clock/language guess is usually right, but a Swede on an English
          phone abroad shouldn't be quoted the wrong one. */}
      {available.length > 1 && (
        <p style={{ color: 'var(--ml-text-secondary)', fontSize: 12, marginTop: 4 }}>
          {pickingCurrency ? (
            <label>
              {t.pricesIn}{' '}
              <select
                aria-label={t.currency}
                value={currency}
                onChange={(e) => { prices.setCurrency(e.target.value); setPickingCurrency(false) }}
                style={{ fontSize: 12 }}
              >
                {available.map((c) => <option key={c} value={c}>{currencyLabel(c)}</option>)}
              </select>
            </label>
          ) : (
            <>
              {t.pricesIn} {currencyLabel(currency)}{' '}
              <button onClick={() => setPickingCurrency(true)} style={{ ...textButton, fontSize: 12, padding: 0 }}>
                {t.change}
              </button>
            </>
          )}
        </p>
      )}
    </section>
  )
}

function PlanCard({ name, price, per, note, alt, features, featured = false, cta, onCta }: {
  name: string; price: string; per: string; note?: string | null; alt?: string
  features: readonly string[]; featured?: boolean; cta?: string; onCta?: () => void
}) {
  return (
    <div style={{
      ...card, marginBottom: 0, padding: 20, display: 'flex', flexDirection: 'column',
      border: featured ? '1.5px solid var(--ml-accent-fg)' : '1px solid var(--ml-separator)',
    }}>
      <h3 className="ml-label" style={{ color: 'var(--ml-text)' }}>
        {name}
      </h3>
      <p className="ml-num" style={{ fontSize: 40, fontWeight: 800, fontStretch: 'var(--ml-hero-stretch)', marginTop: 8, lineHeight: 1 }}>
        {price}
        <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--ml-text-secondary)', marginLeft: 6 }}>{per}</span>
      </p>
      {(note || alt) && (
        <p style={{ fontSize: 12, color: 'var(--ml-text-secondary)', marginTop: 4, lineHeight: 1.4 }}>
          {note}{note && alt && <br />}{alt}
        </p>
      )}
      <ul style={{ margin: '16px 0 0', padding: 0, listStyle: 'none', flex: 1 }}>
        {features.map((f) => (
          <li key={f} style={{ display: 'flex', gap: 8, fontSize: 14, lineHeight: 1.4, marginBottom: 8 }}>
            <span aria-hidden="true" style={{ color: 'var(--ml-accent-fg)', flexShrink: 0, display: 'flex', paddingTop: 1 }}><CheckIcon size={16} /></span>
            {f}
          </li>
        ))}
      </ul>
      {cta && (
        <button
          onClick={onCta}
          className="ml-btn ml-btn--wide ml-btn--primary"
          style={{ marginTop: 16 }}
        >
          {cta}
        </button>
      )}
    </div>
  )
}

// ── Shared bits ───────────────────────────────────────────────────────────────

const card: CSSProperties = {
  background: 'var(--ml-surface)', border: '1px solid var(--ml-separator)',
  borderRadius: 'var(--ml-radius-card)', overflow: 'hidden', marginBottom: 16,
}

const textButton: CSSProperties = {
  padding: 8, border: 'none', background: 'transparent', cursor: 'pointer',
  color: 'var(--ml-accent-fg)', fontSize: 14,
}
